import { useEffect, useRef } from "react"
import { Link, useLocation } from "react-router-dom"
import { cn } from "@/lib/utils"
import { SUB_VIEW_PARAM, type SubView } from "@/lib/bid-sections"

interface BidSubNavProps<T extends string> {
  label: string
  views: SubView<T>[]
  /** Null when no view is current (e.g. the addendum review surface is open). */
  active: T | null
}

/**
 * Underline sub-nav for a work area (design system: Tabs → Primary tabs).
 * Each view is a `?view=` link so it deep-links and survives a reload. Other
 * params are dropped on purpose: `highlight`, `run` and `item` belong to the
 * view they were opened on.
 */
export function BidSubNav<T extends string>({ label, views, active }: BidSubNavProps<T>) {
  const location = useLocation()
  const navRef = useRef<HTMLElement>(null)
  const activeRef = useRef<HTMLAnchorElement>(null)

  useEffect(() => {
    const revealActive = () => {
      const nav = navRef.current
      const current = activeRef.current
      if (!nav || !current) return
      const bounds = nav.getBoundingClientRect()
      const tab = current.getBoundingClientRect()
      if (tab.left < bounds.left) nav.scrollLeft += tab.left - bounds.left
      else if (tab.right > bounds.right) nav.scrollLeft += tab.right - bounds.right
    }
    revealActive()
    window.addEventListener("resize", revealActive)
    return () => window.removeEventListener("resize", revealActive)
  }, [active])
  return (
    <nav
      ref={navRef}
      aria-label={label}
      className="flex h-11 shrink-0 items-end gap-4 overflow-x-auto border-b border-border bg-card px-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      {views.map((view) => {
        const isActive = view.value === active
        return (
          <Link
            key={view.value}
            ref={isActive ? activeRef : undefined}
            to={{ pathname: location.pathname, search: `?${SUB_VIEW_PARAM}=${encodeURIComponent(view.value)}` }}
            replace
            aria-current={isActive ? "page" : undefined}
            className={cn(
              "-mb-px whitespace-nowrap border-b-2 px-2 py-2.5 text-sm transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              isActive
                ? "border-foreground font-medium text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            {view.label}
          </Link>
        )
      })}
    </nav>
  )
}
